import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import styled, { useTheme, css } from 'styled-components'
import { rem } from 'polished'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'

import { Avatar, ProgressBar } from '@tiltify/design'
import { Currency } from '../../Currency'
import { baseTiltifyUrl, getCampaignUrl } from '../../../utils/tiltifyUrl'
import { uppercaseMessages } from '@tiltify/ui/translations'
import { StreamCard } from './StreamCard'

const UserAvatar = styled(Avatar)<{ width: number }>`
  position: absolute;
  opacity: 0;
  left: 50%;
  margin-left: ${({ width }) => -(width / 2)}px;
  ${({ theme }) => theme.transition[0]}
  transform: translateY(-0.8rem);

  @media (max-width: ${({ theme }) => theme.breakpoints[0]}) {
    opacity: 1;
    bottom: -2rem;
  }
`
const cardCss = css`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  max-width: 592px;
  min-height: 309px;
  border-radius: 4px;
  cursor: pointer;
  overflow: hidden;
  ${({ theme }) => theme.transition[0]}
  color: ${({ theme }) => theme.colors.black.core};
  background-color: ${({ theme }) => theme.colors.white.core};
  box-shadow: ${({ theme }) => theme.depth[4]};
  background: #fff;

  &:hover {
    && div div img:first-of-type {
      transform: scale(1.02);
    }
  }
  &:hover ${UserAvatar} {
    opacity: 1;
    transform: translateY(-2rem);
    box-shadow: ${({ theme }) => theme.depth[16]};
  }
  @media (max-width: 400px) {
    width: 100%;
  }
`
const StyledCampaignCard = styled(Link)`
  ${cardCss}
`

const StyledCampaignCardExternal = styled.a`
  ${cardCss}
`

const StyledLive = styled.div`
  box-sizing: border-box;
  padding: 2px 6px;
  top: 8px;
  left: 8px;
  position: absolute;
  z-index: 2;
  border-radius: 3px;
  background-color: ${({ theme }) => theme.colors.error.core};
  text-align: center;
  color: #fff;
  font-size: ${rem(13)};
  line-height: 1rem;
  text-transform: uppercase;
`

const StyledStreamWrapper = styled.div`
  position: relative;
  justify-content: center;
`

const AmountWrapper = styled.div`
  color: ${({ theme }) => theme.colors.black.core};
  display: flex;
  flex-direction: column;
  line-height: 1.15;
  text-align: left;
  font-size: 0.875rem;
  line-height: ${rem(17)};
  font-weight: 700;
`

const StyledDescription = styled.div`
  text-align: left;
  flex: 1;
  margin-bottom: ${rem(19)};
`

const StyledUserName = styled.div`
  line-height: ${rem(19)};
`

const StyledCauseName = styled.div`
  font-size: ${rem(14)};
  line-height: ${rem(17)};
  color: ${({ theme }) => theme.colors.black[600]};
  margin-bottom: ${rem(8)};
`

const StyledCampaignName = styled.div`
  font-size: 0.875rem;
  line-height: ${rem(17)};
  font-weight: 700;
`

const BottomContentWrapper = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`

interface CampaignCardProps {
  campaign?: TiltifyCampaign | TiltifyTeamEvent
  loading?: boolean
  external?: boolean
  progressColor?: string
}

export const CampaignCard = ({
  campaign,
  loading,
  external = false,
  progressColor,
}: CampaignCardProps): JSX.Element => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const [raised, setRaised] = useState('0')
  const [currency, setCurrency] = useState('USD')
  const [percent, setPercent] = useState(0)

  const live = campaign?.live
  const name = campaign?.name
  const userName =
    campaign && campaign.team ? campaign?.team?.name : (campaign as TiltifyCampaign)?.user?.username

  const userAvatar =
    campaign?.avatar?.src ||
    (campaign && campaign.team
      ? campaign?.team?.avatar?.src
      : (campaign as TiltifyCampaign)?.user?.avatar?.src)
  const url = getCampaignUrl(campaign, true)

  useEffect(() => {
    if (campaign) {
      const { totalAmountRaised, goal } = campaign

      setRaised(totalAmountRaised?.value)
      setCurrency(totalAmountRaised?.currency ?? 'USD')

      let width =
        parseFloat(totalAmountRaised?.value) > 0 && goal?.value
          ? parseFloat(totalAmountRaised.value) / parseFloat(goal.value)
          : 0

      if (width > 1) width = 1

      setPercent(width)
    }
  }, [campaign])

  if (loading) {
    return (
      <SkeletonTheme
        baseColor={theme.isDark ? theme.colors.black[700] : '#d4dce466'}
        highlightColor={theme.isDark ? theme.colors.black[800] : ''}
      >
        <StyledCampaignCard as='div'>
          <StyledStreamWrapper>
            <StreamCard loading={loading} campaign={campaign} />
          </StyledStreamWrapper>
          <BottomContentWrapper>
            <div>
              <AmountWrapper>
                <Skeleton />
              </AmountWrapper>
              <Skeleton />
            </div>
            <StyledDescription>
              <StyledUserName>
                <Skeleton />
              </StyledUserName>
              <StyledCauseName>
                <Skeleton />
              </StyledCauseName>
              <StyledCampaignName>
                <Skeleton />
              </StyledCampaignName>
            </StyledDescription>
          </BottomContentWrapper>
        </StyledCampaignCard>
      </SkeletonTheme>
    )
  }
  const content = (
    <>
      <StyledStreamWrapper>
        {live ? <StyledLive>{formatMessage(uppercaseMessages.live)}</StyledLive> : null}
        <StreamCard campaign={campaign} />
        {userAvatar && (
          <UserAvatar src={userAvatar} height={46} width={46} alt={`${campaign?.name} avatar`} />
        )}
      </StyledStreamWrapper>
      <BottomContentWrapper>
        <StyledDescription>
          <StyledUserName>{userName}</StyledUserName>
          <StyledCauseName>
            {campaign && campaign.team
              ? (campaign as TiltifyTeamEvent).supporting?.[0].cause.name
              : (campaign as TiltifyCampaign)?.cause?.name}
          </StyledCauseName>
          <StyledCampaignName>{name}</StyledCampaignName>
        </StyledDescription>
        <div>
          <AmountWrapper>
            <Currency currency={currency} value={raised} />
          </AmountWrapper>

          <ProgressBar
            background={theme.colors.black[100]}
            foreground={progressColor || theme.colors.green.core}
            percent={percent}
          />
        </div>
      </BottomContentWrapper>
    </>
  )

  if (external)
    return (
      <StyledCampaignCardExternal href={`${baseTiltifyUrl()}${url}`}>
        {content}
      </StyledCampaignCardExternal>
    )

  return <StyledCampaignCard to={url}>{content}</StyledCampaignCard>
}
